<template>
  <div class="wg-modal-back" @click="$emit('close')">
    <div class="wg-modal wide" role="dialog" aria-modal="true" @click.stop>
      <button class="wg-modal-x" aria-label="Close" @click="$emit('close')">
        <i class="material-icons">close</i>
      </button>

      <div class="wg-rshead">
        <div class="wg-rsico"><i class="material-icons-outlined">settings</i></div>
        <h3>{{ frequencyLabel }} Wins report settings</h3>
      </div>

      <div class="wg-rsrow top">
        <div class="txt">
          <div class="l">{{ frequencyLabel }} Wins report</div>
          <div class="d">Your performance report every {{ frequencyWord }}, sent by email</div>
        </div>
        <wg-toggle v-model="enabled" :class="{ 'wg-inert': !canEdit }" />
      </div>

      <div class="wg-rslabel">Frequency</div>
      <div class="wg-freq" :class="{ 'wg-inert': !canEdit }">
        <button
          v-for="option in frequencyOptions"
          :key="option.id"
          class="wg-freq-opt"
          :class="{ active: frequency === option.id }"
          @click="canEdit && (frequency = option.id)"
        >
          <span class="rdo"></span>
          <span class="txt">
            <span class="l">{{ option.label }}</span>
            <span class="d">Every {{ option.word }}</span>
          </span>
        </button>
      </div>

      <div class="wg-rslabel">Reporting countries</div>
      <div class="wg-rshelp">
        The whole report covers this scope. Multiple countries are combined, not broken out.
        Changes apply right away; past reports never change.
      </div>
      <wins-country-select
        :selected="reportingCountries"
        :can-edit="canEdit"
        @toggle="toggleCountry"
        @clear="reportingCountries = []"
      />

      <div class="wg-rslabel">Recipients</div>
      <div class="wg-rshelp">Multiple emails separated by comma</div>
      <input class="wg-input-sm wg-rsmail" v-model="recipientEmails" />
      <div v-if="error" class="wg-rserror">{{ error }}</div>

      <div class="acts">
        <button
          v-if="canEdit"
          class="wg-mbtn ghost send-now"
          :disabled="sending || saving"
          :title="'Email the report you are viewing to the recipients right away'"
          @click="$emit('send-now')"
        >{{ sending ? 'Sending…' : 'Send report now' }}</button>
        <div class="sp"></div>
        <button class="wg-mbtn ghost" :disabled="saving" @click="$emit('close')">Cancel</button>
        <button class="wg-mbtn solid" :disabled="saving" @click="save">
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Report-specific controls, on the Wins tab (design_updated6): master
 * toggle, frequency, reporting countries and recipients in one modal,
 * saved together. "When to start counting" is intentionally absent.
 */
import WgToggle from '@/components/SitesSingleView/WhiteGlove/Settings/WgToggle.vue'
import WinsCountrySelect from '@/components/SitesSingleView/WhiteGlove/Settings/WinsCountrySelect.vue'

const FREQUENCY_OPTIONS = [
  { id: 'monthly', label: 'Monthly', word: 'month' },
  { id: 'quarterly', label: 'Quarterly', word: 'quarter' },
]
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export default {
  name: 'WinsReportSettingsModal',
  components: { WgToggle, WinsCountrySelect },
  props: {
    settings: {
      type: Object,
      default: null,
    },
    /** Admins edit everything; others may still edit recipients. */
    canEdit: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    sending: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const settings = this.settings || {}
    return {
      enabled: Boolean(settings.enabled),
      frequency: settings.frequency || 'quarterly',
      reportingCountries: (settings.reportingCountries || '')
        .split(',').map((c) => c.trim()).filter(Boolean),
      recipientEmails: settings.recipientEmails || '',
      error: '',
      frequencyOptions: FREQUENCY_OPTIONS,
    }
  },
  computed: {
    frequencyLabel () {
      return this.frequency === 'monthly' ? 'Monthly' : 'Quarterly'
    },
    frequencyWord () {
      return this.frequency === 'monthly' ? 'month' : 'quarter'
    },
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    onKeydown (event) {
      if (event.key === 'Escape') this.$emit('close')
    },
    toggleCountry (country) {
      this.reportingCountries = this.reportingCountries.includes(country.code)
        ? this.reportingCountries.filter((c) => c !== country.code)
        : [...this.reportingCountries, country.code]
    },
    save () {
      this.error = ''
      const emails = this.recipientEmails.split(',').map((e) => e.trim()).filter(Boolean)
      const invalid = emails.find((e) => !EMAIL_RE.test(e))
      if (invalid) {
        this.error = `"${invalid}" doesn't look like a valid email address.`
        return
      }
      const body = { recipientEmails: emails.join(', ') }
      if (this.canEdit) {
        body.enabled = this.enabled ? 1 : 0
        body.frequency = this.frequency
        body.reportingCountries = this.reportingCountries.join(', ')
      }
      this.$emit('save', body)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../Settings/settings-card.scss';

.wg-modal-back {
  position: fixed;
  inset: 0;
  background: rgba(10, 10, 10, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  padding: 24px;
}

.wg-modal {
  position: relative;
  background: #fff;
  border-radius: 14px;
  box-shadow: $shadow-menu;
  width: 100%;
  padding: 28px 30px 24px;

  &.wide {
    max-width: 560px;
  }
}

.wg-modal-x {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  color: $pop-ink-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    background: $pop-box-bg;
    color: $pop-teal;
  }

  .material-icons {
    font-size: 1.2rem;
  }
}

.wg-rshead {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  padding-right: 26px;

  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    color: $pop-teal;
    margin: 0;
  }
}

.wg-rsico {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: $pop-accent-bg;
  color: $pop-accent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  .material-icons-outlined {
    font-size: 1.5rem;
  }
}

.wg-rsrow {
  display: flex;
  align-items: center;
  gap: 14px;

  &.top {
    padding-bottom: 16px;
    border-bottom: 1px solid $wg-hairline;
    margin-bottom: 4px;
  }

  .txt {
    flex: 1;
  }

  .l {
    font-size: 0.92rem;
    font-weight: 700;
    color: $pop-teal;
  }

  .d {
    font-size: 0.8rem;
    color: $pop-ink-muted;
    margin-top: 2px;
  }
}

/* frequency radio cards (from the retired WinsReportCard) */
.wg-freq {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
}

.wg-freq-opt {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  text-align: left;
  background: #fff;
  border: 1px solid $wg-panel-line;
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: $pop-accent;
  }

  &.active {
    border-color: $pop-accent;
    background: $pop-accent-bg;

    .rdo {
      border-color: $pop-accent;
      border-width: 5px;
    }
  }

  .rdo {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1.5px solid $pop-border;
    flex: none;
    margin-top: 2px;
    background: #fff;
    box-sizing: border-box;
  }

  .txt {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .l {
    font-size: 0.9rem;
    font-weight: 700;
    color: $pop-teal;
  }

  .d {
    font-size: 0.8rem;
    color: $pop-ink-muted;
  }
}

.wg-rslabel {
  font-size: 0.78rem;
  font-weight: 700;
  color: $pop-ink-faint;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 18px 0 6px;
}

.wg-rshelp {
  font-size: 0.8rem;
  color: $pop-ink-muted;
  margin-bottom: 10px;
  line-height: 1.5;
}

.wg-rsmail {
  width: 100%;
}

.wg-rserror {
  font-size: 0.8rem;
  color: $pop-danger;
  margin-top: 8px;
}

.wg-inert {
  pointer-events: none;
  opacity: 0.6;
}

.acts {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 22px;

  .sp {
    flex: 1;
  }
}

.wg-mbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  padding: 0 24px;
  border-radius: 30px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;

  &.ghost {
    background: transparent;
    color: $pop-teal;
    border-color: $pop-border;

    &:hover:not(:disabled) {
      background: $pop-box-bg;
    }
  }

  &.solid {
    background: $pop-accent;
    color: #fff;

    &:hover:not(:disabled) {
      opacity: 0.85;
    }
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
}
</style>
